import React, { useState } from "react";
import { executePublicMutation } from "../../../graphql/client";
import { captureRefCode } from "../../../referral";
import { capture } from "../../../analytics";

// /win — "share a post, win a membership" entry page for Instagram.
// The real purpose is the IG handle: David evaluates the profile and
// decides, case by case, who gets a free or discounted invite code.
// Asks for the minimum that makes that decision possible (handle +
// email); phone is optional on purpose — asking for it up front costs
// more entries than it's worth.

const IG_HANDLE = process.env.REACT_APP_INSTAGRAM_HANDLE || "activebeaches";

const field = {
  width: "100%",
  padding: "11px 12px",
  border: "1px solid #c9d0dc",
  borderRadius: 8,
  fontSize: 16,
  boxSizing: "border-box",
};

const Win = () => {
  const [igHandle, setIgHandle] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const handle = igHandle.trim().replace(/^@+/, "");
    if (!/^[A-Za-z0-9._]{1,30}$/.test(handle)) {
      setError("That doesn't look like an Instagram username (letters, numbers, dots, underscores).");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError("Please enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      await executePublicMutation(
        `mutation EnterIgContest($input: IgEntryInput!) {
          enterIgContest(input: $input)
        }`,
        {
          input: {
            igHandle: handle,
            email: email.trim().toLowerCase(),
            phone: phone.trim() || null,
            sharedPost: shared,
            refCode: captureRefCode() || null,
          },
        }
      );
      capture("ig_contest_entered", { shared });
      setDone(true);
    } catch (err) {
      setError("Something went wrong — try again in a minute.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 520, margin: "0 auto", padding: "28px 16px 48px" }}>
      <h1
        style={{
          fontFamily: "var(--ab-font-display)",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          fontWeight: 400,
          fontSize: 38,
          color: "var(--ab-ink)",
          marginBottom: 6,
          textAlign: "center",
        }}
      >
        Win a membership 🌴
      </h1>
      <p style={{ textAlign: "center", color: "#5b667a", fontSize: 15, marginBottom: 22 }}>
        Share one of our posts to your story, tag <strong>@{IG_HANDLE}</strong>,
        and drop your handle below. We pick winners for free and discounted
        memberships every week — and everyone who enters gets first dibs on
        tourney nights and new events.
      </p>

      {done ? (
        <div
          style={{
            background: "#eef7ee",
            border: "1px solid #b9dcb5",
            borderRadius: 10,
            padding: "18px 16px",
            color: "#1f4f1d",
            fontSize: 15,
            lineHeight: 1.5,
          }}
        >
          <strong>You're entered! 🎉</strong>
          <div style={{ marginTop: 8 }}>
            Make sure your story tags <strong>@{IG_HANDLE}</strong> so we can
            find it. If you win, your code arrives by Instagram DM or email —
            keep an eye on both. Want to skip the wait?{" "}
            <a href="/signup" style={{ color: "#1f7a3a", fontWeight: 700 }}>
              Join today
            </a>
            .
          </div>
        </div>
      ) : (
        <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 700, color: "#3a4150" }}>
            Your Instagram
            <div style={{ position: "relative", marginTop: 4 }}>
              <span
                style={{
                  position: "absolute",
                  left: 12,
                  top: 11,
                  color: "#8b94a6",
                  fontSize: 16,
                }}
              >
                @
              </span>
              <input
                style={{ ...field, paddingLeft: 28 }}
                value={igHandle}
                onChange={(e) => setIgHandle(e.target.value)}
                placeholder="yourhandle"
                autoCapitalize="none"
                autoCorrect="off"
                required
              />
            </div>
          </label>
          <label style={{ fontSize: 13, fontWeight: 700, color: "#3a4150" }}>
            Email
            <input
              style={{ ...field, marginTop: 4 }}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </label>
          <label style={{ fontSize: 13, fontWeight: 700, color: "#3a4150" }}>
            Phone <span style={{ fontWeight: 400, color: "#8b94a6" }}>(optional — for a text when you win)</span>
            <input
              style={{ ...field, marginTop: 4 }}
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(904) 555-1234"
            />
          </label>
          <label
            style={{
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
              fontSize: 14,
              color: "#3a4150",
            }}
          >
            <input
              type="checkbox"
              checked={shared}
              onChange={(e) => setShared(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span>
              I shared an @{IG_HANDLE} post to my story (doubles your odds)
            </span>
          </label>
          {error && (
            <div style={{ color: "#b02a2a", fontSize: 14 }}>{error}</div>
          )}
          <button
            type="submit"
            disabled={busy}
            style={{
              padding: "13px 18px",
              background: "#1f7a3a",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontWeight: 700,
              fontSize: 16,
              cursor: busy ? "default" : "pointer",
            }}
          >
            {busy ? "Entering…" : "Enter to win"}
          </button>
          <div style={{ fontSize: 12, color: "#8b94a6", textAlign: "center" }}>
            We only use this to pick winners and send your code. No spam, ever.
          </div>
        </form>
      )}
    </div>
  );
};

export default Win;
